import {useCallback, useRef, useMemo} from 'react'
import {useLocalStorage} from '@/shared/lib'
import {STORAGE_KEYS} from '@/shared/config'
import {NoiseGateProcessor} from './noiseGateProcessor'
import {DEFAULT_THRESHOLD} from './types'
import type {InputSensitivityMode} from './types'

/**
 * Manages audio settings state (threshold, mode) with localStorage persistence
 * and owns the singleton NoiseGateProcessor instance.
 *
 * Must be used inside a component that lives for the duration of the room session.
 */
export function useAudioSettings() {
  const [threshold, setThreshold] = useLocalStorage<number>(
    STORAGE_KEYS.AUDIO_THRESHOLD,
    DEFAULT_THRESHOLD,
  )

  const [mode, setMode] = useLocalStorage<InputSensitivityMode>(
    STORAGE_KEYS.AUDIO_SENSITIVITY_MODE,
    'automatic',
  )

  // Singleton processor — survives re-renders, created once
  const processorRef = useRef<NoiseGateProcessor | null>(null)
  if (!processorRef.current) {
    processorRef.current = new NoiseGateProcessor()
  }
  const processor = processorRef.current

  // Keep processor in sync with React state
  processor.setThreshold(threshold)
  processor.setManualMode(mode === 'manual')

  const handleThresholdChange = useCallback(
    (value: number) => {
      setThreshold(value)
      processor.setThreshold(value)
    },
    [setThreshold, processor],
  )

  const handleModeChange = useCallback(
    (newMode: InputSensitivityMode) => {
      setMode(newMode)
      processor.setManualMode(newMode === 'manual')
    },
    [setMode, processor],
  )

  return useMemo(
    () => ({
      threshold,
      setThreshold: handleThresholdChange,
      mode,
      setMode: handleModeChange,
      processor,
    }),
    [threshold, handleThresholdChange, mode, handleModeChange, processor],
  )
}
